import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, FileMinus, Search, X } from "lucide-react";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import Loader from "../Loader";
import "./InvoiceStyle.css";

const formatDate = (d) => {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

const formatAmount = (n) =>
  `₹${parseFloat(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function CreditNotes() {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const user = getDecryptedUserFromCookie("gta_mainstateuser");
  const token = decryptToken("accessToken") || "";
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.main_id) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${url}/newinvoice/all/${user.main_id}?document_type=credit_note`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        setNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Could not load credit notes");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = notes.filter((n) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      String(n.invoice_number || "").toLowerCase().includes(s) ||
      String(n.customer_name || "").toLowerCase().includes(s)
    );
  });

  const totalAmount = useMemo(
    () => notes.reduce((sum, n) => sum + (parseFloat(n.grand_total) || 0), 0),
    [notes]
  );

  if (loading) return <Loader />;

  const isEmpty = notes.length === 0;
  const noSearchResults = !isEmpty && filtered.length === 0;

  return (
    <div className="product-form-page product-form-page--credit-notes">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">Credit notes</h1>
          <p className="product-form-page__subtitle">
            Issue credits against invoices with a remaining balance to adjust what customers owe.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/invoice")}
          >
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Invoices
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Search credit notes"
            onClick={() => setShowSearch((v) => !v)}
          >
            <Search size={18} />
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="quotes-page__search">
          <input
            type="search"
            className="quotes-page__search-input"
            placeholder="Search credit note or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="quotes-page__search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearch("");
              setShowSearch(false);
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {!isEmpty && !noSearchResults ? (
        <div className="credit-notes-page__summary">
          <div className="credit-notes-page__summary-item">
            <span className="credit-notes-page__summary-label">Total credit notes</span>
            <span className="credit-notes-page__summary-value">{notes.length}</span>
          </div>
          <div className="credit-notes-page__summary-item">
            <span className="credit-notes-page__summary-label">Total credited</span>
            <span className="credit-notes-page__summary-value">{formatAmount(totalAmount)}</span>
          </div>
        </div>
      ) : null}

      {isEmpty ? (
        <div className="quotes-page__empty" role="status">
          <FileMinus size={40} strokeWidth={1.25} />
          <p>No credit notes yet. Open an invoice with a remaining balance and issue a credit note from there.</p>
          <button type="button" className="oms-add-btn" onClick={() => navigate("/invoice")}>
            <ArrowLeft size={16} style={{ marginRight: 4 }} />
            Go to invoices
          </button>
        </div>
      ) : noSearchResults ? (
        <div className="quotes-page__empty" role="status">
          <Search size={36} strokeWidth={1.25} />
          <p>No credit notes match &ldquo;{search}&rdquo;. Try a different search term.</p>
          <button
            type="button"
            className="quotes-page__action-btn"
            onClick={() => setSearch("")}
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="quotes-page__table-wrap">
          <table className="quotes-page__table">
            <thead>
              <tr>
                <th>Credit note #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Related invoice</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => (
                <tr key={n.id}>
                  <td>{n.invoice_number}</td>
                  <td>{n.customer_name || "—"}</td>
                  <td>{formatDate(n.invoice_date)}</td>
                  <td>{formatAmount(n.grand_total)}</td>
                  <td>
                    {n.linked_invoice_id ? (
                      <button
                        type="button"
                        className="quotes-page__action-btn"
                        onClick={() => navigate(`/invoice-detail/${n.linked_invoice_id}`)}
                      >
                        View invoice
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <div className="quotes-page__actions">
                      <button
                        type="button"
                        className="quotes-page__action-btn quotes-page__action-btn--primary"
                        onClick={() => navigate(`/invoice-detail/${n.id}`)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
